import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiUsageApi } from '@/features/ai-usage/api';
import { AiUsageDetail } from '@/features/ai-usage/detail';
import { AiUsageFeature, type AiUsageFeatureRow } from '@/features/ai-usage/feature';
import { AiUsageOperation } from '@/features/ai-usage/operation';
import type { AiUsageEvent, AiUsageResponse } from '@/features/ai-usage/types';
import { DetailDrawer } from '@/features/project-board/components/DetailDrawer';
import { Pagination } from '@/shared/ui/Pagination';

const DAY_MS = 24 * 60 * 60 * 1_000;
const PAGE_SIZES = [20, 50, 100];

function inputDate(date: Date): string { return date.toISOString().slice(0, 10); }
function number(value: number | null): string { return value === null ? '—' : value.toLocaleString('ko-KR'); }
function dollars(value: number): string { return value ? `$${value.toFixed(4)}` : '—'; }
function kst(value: string): string { return value ? new Date(value).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) : '—'; }

export function AiUsageView(): React.JSX.Element {
  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(inputDate(new Date(today.getTime() - 6 * DAY_MS)));
  const [to, setTo] = useState(inputDate(today));
  const [userId, setUserId] = useState('');
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState<AiUsageResponse | null>(null);
  const [selected, setSelected] = useState<AiUsageEvent | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(50);

  // 조건은 조회 버튼으로만 적용하고, 페이지·건수 바꾸기는 지금 조건 그대로 다시 부른다.
  const load = useCallback((page = 1, pageSize = size) => {
    setLoading(true);
    AiUsageApi.fetch({
      from: new Date(`${from}T00:00:00+09:00`).toISOString(),
      to: new Date(`${to}T23:59:59.999+09:00`).toISOString(),
      userId: userId.trim() || undefined,
      provider: provider || undefined,
      status: status || undefined,
      page,
      size: pageSize,
    }).then((result) => {
      setData(result); setError(''); setSelected((current) => result.events.find((event) => event.invocationId === current?.invocationId) ?? null);
    }).catch((reason: Error) => setError(reason.message)).finally(() => setLoading(false));
  }, [from, provider, size, status, to, userId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 기본 기간만 자동 조회하고 이후에는 조회 버튼으로 명시 적용한다.
  useEffect(() => { load(); }, []);

  const totals = data?.totals;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;
  const detail = useMemo(() => selected ? AiUsageDetail.payload(selected) : null, [selected]);

  return <div className="space-y-4">
    <header className="flex flex-wrap items-end justify-between gap-2">
      <div><h1 className="text-xl font-bold">Slack 봇 AI 사용량</h1><p className="text-[12px] text-muted">Slack 멘션으로 들어온 요청별 사용자, 토큰 사용량과 질문·응답 내용을 확인합니다.</p></div>
      <span className="text-[12px] text-muted">{data ? `총 ${number(data.total)}건` : ''}</span>
    </header>

    <section className="flex flex-wrap items-end gap-2 rounded-lg border border-line bg-surface p-3 text-[12px]">
      <Filter label="시작일"><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px]" /></Filter>
      <Filter label="종료일"><input type="date" value={to} onChange={(event) => setTo(event.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px]" /></Filter>
      <Filter label="Slack 사용자"><input value={userId} onChange={(event) => setUserId(event.target.value)} placeholder="U0123..." className="w-36 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px]" /></Filter>
      <Filter label="제공자"><select value={provider} onChange={(event) => setProvider(event.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px]"><option value="">전체</option><option value="openai">OpenAI</option><option value="anthropic">Anthropic</option></select></Filter>
      <Filter label="상태"><select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px]"><option value="">전체</option><option value="success">성공</option><option value="error">오류</option></select></Filter>
      <button type="button" onClick={() => load()} disabled={loading} className="rounded-md bg-accent px-4 py-1.5 font-medium text-white disabled:opacity-50">{loading ? '조회 중…' : '조회'}</button>
    </section>

    {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-[12px] text-rose-700">{error}</div>}
    {totals && <section className="grid gap-3 xl:grid-cols-3">
      <ChartCard title="호출 및 오류" description={`전체 ${number(totals.calls)}회 · 성공과 오류 건수`}>
        <ColumnChart data={[{ label: '성공', value: Math.max(0, totals.calls - totals.errors) }, { label: '오류', value: totals.errors }]} color="#10b981" />
      </ChartCard>

      <ChartCard title="토큰 사용량" description="입력과 출력 토큰을 같은 눈금으로 비교">
        <ColumnChart data={[{ label: '입력 토큰', value: totals.inputTokens }, { label: '출력 토큰', value: totals.outputTokens }]} colors={['#3b82f6', '#8b5cf6']} />
      </ChartCard>

      <ChartCard title="비용" description="누적 비용과 호출당 평균 비용">
        <ColumnChart data={[{ label: '누적 비용', value: totals.costUsd }, { label: '호출당 평균', value: totals.calls > 0 ? totals.costUsd / totals.calls : 0 }]} colors={['#f59e0b', '#fb923c']} format={dollars} emptyLabel="비용 데이터가 없습니다" />
      </ChartCard>

      <ChartCard title="기능별 요청 비중" description="봇이 처리한 요청 수 기준 · 한 요청이 기능을 여럿 처리하면 각각 센다" className="xl:col-span-1">
        <FeaturePieChart rows={AiUsageFeature.rows(totals.requests.features).filter((row) => row.requests > 0)} />
      </ChartCard>

      <ChartCard title="기능별 요청 수" description={`대분류 · 소분류별 요청 수 · 기능이 정해지지 않은 요청 ${number(totals.requests.unclassified)}건은 제외`} className="xl:col-span-2">
        <FeatureBarChart rows={AiUsageFeature.rows(totals.requests.features)} />
      </ChartCard>
    </section>}

    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table className="w-full text-left text-[12px]"><thead className="border-b border-line bg-line-soft text-muted"><tr><th className="p-3">시각 (KST)</th><th>사용자</th><th>제공자 / 모델</th><th>기능</th><th>토큰 (입력 / 출력)</th><th>비용</th><th className="pr-3">상태</th></tr></thead>
        <tbody>{(data?.events ?? []).map((event) => <tr key={event.invocationId} onClick={() => setSelected(event)} className="cursor-pointer border-b border-line hover:bg-line-soft last:border-0"><td className="whitespace-nowrap p-3">{kst(event.occurredAt)}</td><td className="font-mono">{event.userId ?? '미상'}</td><td><div>{event.provider}</div><div className="text-[10px] text-faint">{event.models.join(', ') || '—'}</div></td><td>{AiUsageOperation.label(event.operation)}</td><td>{number(event.inputTokens)} / {number(event.outputTokens)}</td><td>{dollars(event.costUsd ?? 0)}</td><td className={event.status === 'error' ? 'pr-3 text-rose-600' : 'pr-3 text-emerald-600'}>{event.status}</td></tr>)}</tbody>
      </table>
      {!error && !loading && (data?.events.length ?? 0) === 0 && <div className="p-8 text-center text-muted">조건에 맞는 사용 내역이 없습니다.</div>}
    </div>
    <div className="flex items-center justify-between gap-2">
      <label className="flex items-center gap-2 text-[12px] text-muted">페이지당<select value={size} onChange={(event) => { const next = Number(event.target.value); setSize(next); load(1, next); }} className="rounded-md border border-line bg-surface px-2 py-1 text-[12px]">{PAGE_SIZES.map((value) => <option key={value} value={value}>{value}건</option>)}</select></label>
      <Pagination page={data?.page ?? 1} totalPages={totalPages} onChange={(next) => load(next)} />
    </div>

    <DetailDrawer payload={detail} onClose={() => setSelected(null)} />
  </div>;
}

function Filter({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return <label className="flex flex-col gap-1 text-muted"><span>{label}</span>{children}</label>;
}

function ChartCard({ title, description, className = '', children }: { title: string; description: string; className?: string; children: React.ReactNode }): React.JSX.Element {
  return <section className={`rounded-lg border border-line bg-surface p-3 ${className}`}>
    <h2 className="text-[13px] font-semibold">{title}</h2>
    <p className="mb-4 mt-0.5 text-[10px] text-muted">{description}</p>
    {children}
  </section>;
}

type ChartDatum = { label: string; value: number };

function ColumnChart({ data, color, colors, format = number, emptyLabel = '조회된 값이 없습니다' }: { data: ChartDatum[]; color?: string; colors?: string[]; format?: (value: number) => string; emptyLabel?: string }): React.JSX.Element {
  const width = 420;
  const height = 200;
  const left = 58;
  const right = 12;
  const top = 16;
  const bottom = 48;
  const plotHeight = height - top - bottom;
  const plotWidth = width - left - right;
  const max = Math.max(...data.map((datum) => datum.value), 1);
  const ticks = [0, max / 2, max];
  const slot = plotWidth / Math.max(1, data.length);
  const barWidth = Math.min(64, slot * 0.52);
  const empty = data.every((datum) => datum.value === 0);

  return <svg viewBox={`0 0 ${width} ${height}`} className="h-48 w-full" role="img" aria-label={data.map((datum) => `${datum.label} ${format(datum.value)}`).join(', ')}>
    {ticks.map((tick) => {
      const y = top + plotHeight - (tick / max) * plotHeight;
      return <g key={tick}>
        <line x1={left} x2={width - right} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
        <text x={left - 8} y={y + 4} textAnchor="end" fill="#64748b" fontSize="10">{format(tick)}</text>
      </g>;
    })}
    {data.map((datum, index) => {
      const barHeight = (datum.value / max) * plotHeight;
      const x = left + slot * index + (slot - barWidth) / 2;
      const y = top + plotHeight - barHeight;
      return <g key={datum.label}>
        <rect x={x} y={y} width={barWidth} height={barHeight} rx="4" fill={colors?.[index] ?? color ?? '#3b82f6'} />
        <text x={x + barWidth / 2} y={Math.max(top + 11, y - 5)} textAnchor="middle" fill="#334155" fontSize="10">{format(datum.value)}</text>
        <text x={x + barWidth / 2} y={height - 15} textAnchor="middle" fill="#64748b" fontSize="10">{datum.label}</text>
      </g>;
    })}
    {empty && <text x={left + plotWidth / 2} y={top + plotHeight / 2} textAnchor="middle" fill="#94a3b8" fontSize="11">{emptyLabel}</text>}
  </svg>;
}

const PIE_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#d97706', '#059669', '#0891b2', '#4f46e5', '#65a30d', '#be123c'];

function FeaturePieChart({ rows }: { rows: AiUsageFeatureRow[] }): React.JSX.Element {
  const total = rows.reduce((sum, entry) => sum + entry.requests, 0);
  const cx = 90;
  const cy = 90;
  const radius = 78;
  let angle = -Math.PI / 2;
  const slices = total > 0 ? rows.map((entry, index) => {
    const start = angle;
    const sweep = total > 0 ? (entry.requests / total) * Math.PI * 2 : 0;
    angle += sweep;
    const end = angle;
    const x1 = cx + radius * Math.cos(start);
    const y1 = cy + radius * Math.sin(start);
    const x2 = cx + radius * Math.cos(end);
    const y2 = cy + radius * Math.sin(end);
    const largeArc = sweep > Math.PI ? 1 : 0;
    const path = sweep >= Math.PI * 2
      ? `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 1 ${cx - 0.01} ${cy - radius} Z`
      : `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    return { ...entry, path, color: PIE_COLORS[index % PIE_COLORS.length] };
  }) : [];

  return <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
    <svg viewBox="0 0 180 180" className="size-44 shrink-0" role="img" aria-label={`기능별 요청 비중, 전체 ${number(total)}건`}>
      {total === 0 && <g><circle cx={cx} cy={cy} r={radius} fill="#e2e8f0" /><text x={cx} y={cy + 4} textAnchor="middle" fill="#64748b" fontSize="11">데이터 없음</text></g>}
      {slices.map((slice) => <path key={slice.key} d={slice.path} fill={slice.color} stroke="white" strokeWidth="1.5"><title>{`${slice.label} ${number(slice.requests)}건 (${(slice.requests / total * 100).toFixed(1)}%)`}</title></path>)}
    </svg>
    <ul className="grid w-full grid-cols-1 gap-x-3 gap-y-1 text-[10px] sm:grid-cols-2">
      {slices.map((slice) => <li key={slice.key} className="flex min-w-0 items-center gap-1.5">
        <span className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: slice.color }} />
        <span className="min-w-0 flex-1 truncate text-muted">{slice.label}</span>
        <strong className="shrink-0">{number(slice.requests)}</strong>
      </li>)}
    </ul>
  </div>;
}

function FeatureBarChart({ rows }: { rows: AiUsageFeatureRow[] }): React.JSX.Element {
  const width = 760;
  const rowHeight = 32;
  const height = Math.max(100, rows.length * rowHeight + 36);
  const labelWidth = 220;
  const plotWidth = width - labelWidth - 44;
  const max = Math.max(...rows.map((entry) => entry.requests), 1);
  const ticks = [0, max / 2, max];
  const top = 10;
  const bottom = height - 24;

  return <svg viewBox={`0 0 ${width} ${height}`} className="h-auto max-h-[340px] w-full" role="img" aria-label="기능별 요청 수 가로 막대 차트">
    {ticks.map((tick) => {
      const x = labelWidth + (tick / max) * plotWidth;
      return <g key={tick}>
        <line x1={x} x2={x} y1={top} y2={bottom} stroke="#e5e7eb" strokeWidth="1" />
        <text x={x} y={height - 5} textAnchor="middle" fill="#64748b" fontSize="10">{number(tick)}</text>
      </g>;
    })}
    {rows.map((entry, index) => {
      const y = top + index * rowHeight + 4;
      const barWidth = (entry.requests / max) * plotWidth;
      return <g key={entry.key}>
        <text x={labelWidth - 10} y={y + 13} textAnchor="end" fill="#475569" fontSize="11">{entry.label}</text>
        <rect x={labelWidth} y={y} width={barWidth} height="18" rx="3" fill={PIE_COLORS[index % PIE_COLORS.length]} />
        <text x={labelWidth + barWidth + 6} y={y + 13} fill="#334155" fontSize="10">{number(entry.requests)}</text>
      </g>;
    })}
  </svg>;
}
